import { useEffect, useState } from "react";
import { Package, Layers, ClipboardCheck, Search, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../../../../context/AuthContext";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";
import { Driver } from "../../../../../services/api/LaborderService/types";
import ClinicLabtestListingTable from "./LabListingSidebar";
import ClinicLabtestOrderDetailsView from "./LabtestDetails";
import OrderPagination from "../../../../OrderManagement/OrderPagination";

type ClinicLabTab = "clinic_assigned" | "Clinic_Ongoing" | "Completed";

const TABS: ClinicLabTab[] = ["clinic_assigned", "Clinic_Ongoing", "Completed"];

const TAB_LABELS: Record<ClinicLabTab, string> = {
  clinic_assigned: "Pending",
  Clinic_Ongoing: "Ongoing",
  Completed: "Completed",
};

const TAB_ICONS: Record<ClinicLabTab, React.ReactNode> = {
  clinic_assigned: <Layers size={14} />,
  Clinic_Ongoing: <Package size={14} />,
  Completed: <ClipboardCheck size={14} />,
};

const LabtestOrder = () => {
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  const [activeTab, setActiveTab] = useState<ClinicLabTab>("clinic_assigned");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, totalPages: 0, totalCount: 0 });
  const [searchText, setSearchText] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);

  // Navigation screens
  const [subView, setSubView] = useState<"list" | "view">("list");
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [detailedOrderInfo, setDetailedOrderInfo] = useState<any | null>(null);

  // Drivers
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string>();

  useEffect(() => {
    fetchOrders();
  }, [activeTab, pagination.page, searchText, refreshCount]);

  useEffect(() => {
    if (clinicId) fetchAvailableDrivers();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await LabService.getAdminLabtestOrderListing(
        pagination.page,
        pagination.pageSize,
        activeTab,
        clinicId,
        searchText
      );
      if (response.success && response.data) {
        setOrders(response.data.ordersList || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.pagination?.totalPages || 0,
          totalCount: response.data.pagination?.totalCount || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching labtest orders:", error);
      toast.error("Failed to fetch labtest orders.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const response = await LabService.getAvailableDrivers(clinicId, "pickup");
      if (response.success) {
        setDrivers(response.data);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const handleTabChange = (tab: ClinicLabTab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSubView("list");
    setSelectedOrder(null);
    setDetailedOrderInfo(null);
    setSelectedDriver(undefined);
  };

  const handleAction = async (order: any) => {
    setSelectedOrder(order);
    setSelectedDriver(undefined);
    try {
      setLoading(true);
      const details = await LabService.getLabOrderDetails(order.orderId || order.id);
      if (details.success && details.data) {
        setDetailedOrderInfo(details.data);
        setSubView("view");
      } else {
        toast.error("Could not fetch order details.");
      }
    } catch (error) {
      toast.error("Failed to retrieve order details.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setSubView("list");
    setSelectedOrder(null);
    setDetailedOrderInfo(null);
    setSelectedDriver(undefined);
    fetchOrders();
  };

  const handleAssignDriver = async (orderId: number) => {
    try {
      if (!selectedDriver) {
        toast.error("Please select a driver first.");
        return;
      }
      const response = await LabService.assignDriver({ orderId, driverId: selectedDriver });
      if (response.success) {
        toast.success("Rider assigned successfully!");
        handleBackToList();
        fetchAvailableDrivers();
      }
    } catch (error) {
      toast.error("Failed to assign rider.");
    }
  };

  const handleForwardToLab = async (orderId: number) => {
    try {
      const response = await LabService.forwardToLab(orderId);
      if (response.success) {
        toast.success("Order forwarded to lab successfully!");
        handleBackToList();
      }
    } catch (error) {
      toast.error("Failed to forward order to lab.");
    }
  };

  // Render details sub-view
  if (subView === "view" && detailedOrderInfo) {
    return (
      <ClinicLabtestOrderDetailsView
        orderDetails={detailedOrderInfo}
        onBack={handleBackToList}
        onAssignDriver={handleAssignDriver}
        onForwardToLab={handleForwardToLab}
        drivers={drivers}
        selectedDriver={selectedDriver}
        onDriverSelect={(driver) => setSelectedDriver(driver.id)}
      />
    );
  }

  return (
    <div className="h-full bg-slate-50 rounded-xl flex flex-col overflow-hidden relative">
      {/* Header & Tabs */}
      <div className="px-5 pt-3 pb-0 bg-white border-b border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center text-white">
              <Package size={16} />
            </div>
            <h1 className="text-base font-bold text-slate-800">Clinic Labtest Orders</h1>
          </div>

          {/* Quick Filter Section */}
          <div className="flex items-center gap-3 self-end md:self-auto">
            {/* Search Input */}
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search order ID..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-slate-50"
              />
            </div>
            {/* Reset Button */}
            <button
              onClick={() => {
                setSearchText("");
                setPagination((p) => ({ ...p, page: 1 }));
                setRefreshCount((prev) => prev + 1);
              }}
              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors border-none cursor-pointer"
              title="Reset Filters"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Tabs Bar */}
        <div className="flex">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-xs font-semibold relative whitespace-nowrap transition-colors border-none bg-transparent cursor-pointer ${activeTab === tab ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
                }`}
              onClick={() => handleTabChange(tab)}
            >
              <span className="inline-flex items-center gap-1.5">
                {TAB_ICONS[tab]}
                {TAB_LABELS[tab]}
              </span>
              {activeTab === tab && (
                <span className="ml-1.5 px-1.5 py-px rounded-full text-[0.6rem] font-bold bg-emerald-50 text-emerald-600">
                  {pagination.totalCount}
                </span>
              )}
              {activeTab === tab && (
                <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-emerald-500 to-green-600 rounded-t" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Listing Content Body */}
      <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">
        <ClinicLabtestListingTable
          orders={orders}
          loading={loading}
          activeTab={activeTab}
          tabLabel={TAB_LABELS[activeTab]}
          onAction={handleAction}
          currentPage={pagination.page}
          pageSize={pagination.pageSize}
        />
        <OrderPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
        />
      </div>
    </div>
  );
};

export default LabtestOrder;